import React from 'react';
import Alert from '@amzn/awsui-components-console/alert';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import BindleAccessLinks from 'src/common/authz/BindleAccessLinks';
import { DeniedNamespace } from 'src/common/authz/isAccessDenied';
import { resolveBindleAccessLink } from 'src/common/utils/bindleLinks';
import { useTranslation } from 'src/i18n/shim/useTranslation';

/**
 * The alert's only UI-owned wording. Generic, so it classifies the failure without competing
 * with the service's sentence below it, which is where the reason is stated.
 */
const ACCESS_DENIED_HEADER_ID = 'access-denied-header';

interface AccessDeniedAlertProps {
  /**
   * The refusal text exactly as the backend sent it (`error.message`).
   *
   * Rendered verbatim: the service decides how much to reveal about a resource
   * the caller may not see, so the UI neither rewords nor supplements it.
   */
  readonly message: string;
  /**
   * The namespace the refusal named, from `getAccessDeniedNamespace(error)`.
   *
   * Optional and routinely absent — the service omits it for search denials,
   * identity-unresolved denials, and namespaces whose Bindle resource could not
   * be looked up. Absent renders the message alone.
   */
  readonly deniedNamespace?: DeniedNamespace;
  /** Forwarded to the wrapper so a page can target its own alert in a test. */
  readonly 'data-testid'?: string;
}

/**
 * A backend authorization refusal, rendered in place of the section that was
 * refused, with a route to request access where the service offered one.
 *
 * SECTION level, not page level: the app shell (nav, header, breadcrumbs) stays
 * around it, so a user refused one namespace can still navigate to one they do
 * have. `StatusScreen`'s `FailureStatus` is the shell-less bootstrap treatment
 * and centres itself at a fixed width, which reads as a misplaced dialog here.
 *
 * NOT dismissible and offering no Retry: nothing behind this alert is
 * renderable, and the identical request would be refused again. The Bindle link
 * is the one thing that can change the answer.
 *
 * The `role="alert"` wrapper is load-bearing: Cloudscape's `Alert` sets no ARIA
 * role of its own, so dropping the div downgrades the refusal to an unannounced
 * region.
 */
const AccessDeniedAlert: React.FC<AccessDeniedAlertProps> = ({
  message,
  deniedNamespace,
  'data-testid': dataTestId,
}) => {
  const { t } = useTranslation();
  // Resolved once and used for both decisions — whether to switch to the
  // with-link layout, and what to render in it — so the alert can never wrap the
  // message in a container that turns out to hold nothing.
  const link = resolveBindleAccessLink(deniedNamespace);

  return (
    <div role="alert" data-testid={dataTestId}>
      <Alert
        type="error"
        header={t(ACCESS_DENIED_HEADER_ID)}
        statusIconAriaLabel={t('status-icon-error')}
      >
        {link ? (
          <SpaceBetween size="xs">
            {/* The service's sentence stays alone in its own block, so the link
                below is an addition rather than a suffix that reads as part of
                the refusal wording. */}
            <span>{message}</span>
            <BindleAccessLinks link={link} />
          </SpaceBetween>
        ) : (
          message
        )}
      </Alert>
    </div>
  );
};

export default AccessDeniedAlert;
